import { computed, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useSettingsStore } from '@/stores/settings'
import { useAuthStore } from '@/sync/auth'
import { getSupabase } from '@/sync/supabase'
import { createSyncedDoc, type SyncedDoc } from '@/sync/yjs-sync'
import { connectCollection } from '@/sync/connect'
import * as repo from '@/db/repositories'
import type { Shelf } from '@/types/domain'

/**
 * Composable que sincroniza las estanterías del usuario vía CRDT.
 *
 * A diferencia de `useBookSync` (un doc por libro), aquí hay un ÚNICO
 * doc global por usuario (room `shelves:${userId}`) que contiene un
 * `Y.Array<Shelf>`. Las estanterías son many-to-many y no dependen de
 * un libro abierto, así que conviven durante toda la sesión.
 *
 * Reutiliza `connectCollection` (que soporta add/remove/update por id)
 * y `repo.reconcileShelves` como persistencia local de los cambios
 * remotos.
 */

/** Doc activo a nivel de módulo (singleton global de estanterías). */
let current: { synced: SyncedDoc; cleanup: () => void } | null = null

// Flush final al cerrar la pestaña, igual que en useSync: evita perder
// los últimos ~2s de ediciones que el debounce no alcanzó a persistir.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    current?.synced.flushBeacon()
  })
}

export function useShelvesSync() {
  const settings = useSettingsStore()
  const auth = useAuthStore()
  const { settings: settingsRefs } = storeToRefs(settings)

  const syncEnabled = computed(
    () => settingsRefs.value.sync.enabled && auth.isAuthenticated,
  )

  /**
   * Abre el doc global de estanterías y lo conecta al ref del store.
   * Devuelve true si el sync quedó activo; false si no hay sesión/sync.
   * Idempotente: si ya está abierto, no hace nada.
   */
  function openShelvesSync(shelvesRef: Ref<Shelf[]>): boolean {
    if (!syncEnabled.value || !auth.userId) return false

    const client = getSupabase(settingsRefs.value.sync)
    if (!client) return false

    // Ya abierto: no reiniciar.
    if (current) return true

    const room = `shelves:${auth.userId}`
    const syncCfg = settingsRefs.value.sync
    const restBase = syncCfg.supabaseUrl ? `${syncCfg.supabaseUrl.replace(/\/$/, '')}/rest/v1` : undefined
    const synced = createSyncedDoc(room, client, auth.userId, restBase, syncCfg.supabaseAnonKey)
    current = { synced, cleanup: () => {} }

    // Persistencia local de cambios remotos (el CRDT es la fuente de verdad).
    connectCollection(shelvesRef, synced, 'shelves', repo.reconcileShelves)
      .then((result) => {
        if (current) {
          current.cleanup = result.disconnect
        } else {
          // Se cerró mientras conectábamos; limpiar.
          result.disconnect()
        }
      })
      .catch((e) => console.warn('[sync-shelves] conexión falló:', e))

    return true
  }

  /** Cierra el doc de estanterías (persiste y desconecta). */
  function closeShelvesSync() {
    if (current) {
      current.cleanup()
      current.synced.destroy()
      current = null
    }
  }

  return { syncEnabled, openShelvesSync, closeShelvesSync }
}
